/**
 * Custom Embla Carousel slider element with thumbnails, autoplay, dots, arrows, and responsive features.
 * @extends HTMLElement
 */
class bdSlider extends HTMLElement {
  // -----------------------------
  // STATIC CONSTANTS
  // -----------------------------
  static #BREAKPOINTS = {
    MOBILE: 749,
    TABLET: 1024,
    MOBILE_ONLY: 480,
  };

  static #DISABLE_SETTINGS = ['mobile', 'tablet', 'desktop', 'tablet-up', 'mobile-only'];
  static #EDGE_DISTANCES = { NEAR: 150, EXTREME: 50 };
  static #DEFAULT_SPEED = 7;
  static #DEFAULT_SCROLL_SPEED = 0;

  // -----------------------------
  // PRIVATE FIELDS
  // -----------------------------
  #resizeTimeout = null;
  #resizeHandler = () => this.#handleResize();
  #wheelHandler = (e) => this.#onWheel(e);
  #dom = {};
  #emblaSubscriptions = new Set();

  embla = null;
  thumbsEmbla = null;
  autoplayId = null;

  // -----------------------------
  // PUBLIC API
  // -----------------------------
  /**
   * Initializes the slider and all its features.
   * @public
   */
  initSlider() {
    this.#init();
  }

  /**
   * Destroys the slider and cleans up all resources.
   * @public
   */
  destroySlider() {
    this.#destroy();
  }

  // -----------------------------
  // LIFECYCLE HOOKS
  // -----------------------------
  connectedCallback() {
    window.addEventListener('resize', this.#resizeHandler);
    this.#init();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#resizeHandler);
    this.#destroy();
  }

  // -----------------------------
  // INITIALIZATION
  // -----------------------------
  #init() {
    this.#cacheDom();
    if (!this.#dom.slider) {
      console.warn('bdSlider: .bd-slider not found in the component.');
      return;
    }

    if (this.#shouldDisable()) {
      this.#destroy();
      return;
    }

    this.#initEmbla();
    this.#initThumbnails();
    this.#initAutoplay();
    this.#initControls();
    this.#initDots();
    this.#initArrows();
    this.#initCustomPagination();

    requestAnimationFrame(() => this.#updateControls());
  }

  #cacheDom() {
    this.#dom = {
      slider: this.querySelector('.bd-slider'),
      track: this.querySelector('.bd-track'),
      prev: this.querySelector('[data-prev]'),
      next: this.querySelector('[data-next]'),
      dots: this.querySelector('.slider-dots'),
      prevText: this.querySelector('[data-prev-text]'),
      nextText: this.querySelector('[data-next-text]'),
    };
  }

  #shouldDisable() {
    const setting = this.dataset.sliderDisable;
    if (!setting || !bdSlider.#DISABLE_SETTINGS.includes(setting)) return false;

    const w = window.innerWidth;
    return (
      (setting === 'mobile' && w < bdSlider.#BREAKPOINTS.MOBILE) ||
      (setting === 'tablet' && w >= bdSlider.#BREAKPOINTS.MOBILE && w < bdSlider.#BREAKPOINTS.TABLET) ||
      (setting === 'desktop' && w >= bdSlider.#BREAKPOINTS.TABLET) ||
      (setting === 'tablet-up' && w >= bdSlider.#BREAKPOINTS.MOBILE) ||
      (setting === 'mobile-only' && w < bdSlider.#BREAKPOINTS.MOBILE_ONLY)
    );
  }

  // -----------------------------
  // EMBLA INITIALIZATION
  // -----------------------------
  #initEmbla() {
    const opts = this.#buildOptions();
    const plugins = this.#buildPlugins();

    this.#cleanupClones();
    this.#assignSlideUIDs();
    this.#duplicateSlidesIfNeeded();

    this.embla = EmblaCarousel(this.#dom.slider, opts, plugins);

    this.dispatchEvent(
      new CustomEvent('embla-ready', {
        bubbles: true,
        detail: { embla: this.embla },
      }),
    );

    const initial = parseInt(this.dataset.initialSlide);
    if (!isNaN(initial)) this.embla.scrollTo(initial, true);

    this.#subscribeToEmblaEvents();
    this.#dom.slider.addEventListener('wheel', this.#wheelHandler, { passive: false });

    this.#markEdges();

    this.#dom.slider.classList.toggle('no-slides', this.#allSlidesFit());

  }

  #buildOptions() {
    const d = this.dataset;
    const fade = d.fade === 'true';

    return {
      loop: d.wrapAround === 'true',
      align: d.align || 'start',
      draggable: !fade,
      dragFree: fade ? false : d.dragFree === 'true',
      containScroll: fade ? false : d.containScroll || false,
      speed: fade ? 0 : parseInt(d.speed ?? bdSlider.#DEFAULT_SPEED),
      skipSnaps: false,
      inViewThreshold: 0.25,
      containerSelector: '.bd-track',
      slides: '.bd-track > *',
    };
  }

  #buildPlugins() {
    const plugins = [];
    const scrollSpeed = parseFloat(this.dataset.scrollSpeed);
    const fade = this.dataset.fade === 'true';

    if (fade) {
      plugins.push(EmblaCarouselFade());
    } else if (scrollSpeed) {
      plugins.push(
        EmblaCarouselAutoScroll({
          speed: scrollSpeed,
          stopOnInteraction: false,
          stopOnMouseEnter: true,
          target: 'snap',
        }),
      );
    }

    return plugins;
  }

  #assignSlideUIDs() {
    [...this.#dom.track.children].forEach((slide, i) => {
      slide.dataset.uid = i;
    });
  }

  #cleanupClones() {
    this.#dom.track.querySelectorAll('.is-clone').forEach(clone => clone.remove());
  }

  #duplicateSlidesIfNeeded() {
    if (!this.hasAttribute('data-fill-available')) return;

    const slides = [...this.#dom.track.children].filter(
      (s) => !s.classList.contains('is-clone'),
    );

    if (!slides.length) return;

    const viewport = this.#dom.slider.offsetWidth;
    const totalWidth = slides.reduce((sum, s) => sum + s.offsetWidth, 0);

    if (totalWidth < viewport) {
      slides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.classList.add('is-clone');
        this.#dom.track.appendChild(clone);
      });
    }
  }

  #subscribeToEmblaEvents() {
    this.#unsubscribeFromEmblaEvents(); // Clean up any existing subscriptions
    const events = ['select', 'scroll', 'init', 'reInit', 'resize'];
    events.forEach(event => {
      this.embla.on(event, () => this.#handleEmblaEvent(event));
    });
  }

  #handleEmblaEvent(event) {
    switch (event) {
      case 'select':
      case 'scroll':
        this.#markEdges();
        break;
      case 'init':
      case 'reInit':
      case 'resize':
        this.#updateControls();
        break;
    }
  }

  #unsubscribeFromEmblaEvents() {
    this.#emblaSubscriptions.forEach(sub => sub());
    this.#emblaSubscriptions.clear();
  }

    /**
 * Checks if all slides fit within the viewport.
 * @returns {boolean}
 */
#allSlidesFit() {
  if (!this.embla) return false;

  const slides = this.embla.slideNodes().filter(s => !s.classList.contains('is-clone'));
  if (!slides.length) return false;

  const viewportWidth = this.#dom.slider.offsetWidth;
  const totalSlidesWidth = slides.reduce((sum, slide) => sum + slide.offsetWidth, 0);

  return totalSlidesWidth <= viewportWidth;
}
  // -----------------------------
  // THUMBNAILS
  // -----------------------------
  #initThumbnails() {
    const selector = this.dataset.sliderThumbs;
    if (!selector) return;

    const root = document.querySelector(selector);
    if (!root) return;

    this.thumbsEmbla?.destroy();
    this.thumbsEmbla = EmblaCarousel(root, {
      align: 'start',
      containScroll: 'keepSnaps',
      dragFree: false,
      slides: '.bd-track > *',
    });

    const thumbs = this.thumbsEmbla.slideNodes();
    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.embla.scrollTo(i, true));
    });

    this.embla.on('select', () => {
      const index = this.embla.selectedScrollSnap();
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
      this.thumbsEmbla.scrollTo(index, false);
    });
  }

  // -----------------------------
  // AUTOPLAY
  // -----------------------------
  #initAutoplay() {
    const ms = parseInt(this.dataset.autoplay);
    const scrollSpeed = this.dataset.scrollSpeed;

    if (scrollSpeed || !ms) return;
    if (this.autoplayId) this.#stopAutoplay();

    const run = () => this.embla.scrollNext();
    this.autoplayId = setInterval(run, ms);

    const stop = () => this.#stopAutoplay();
    const resume = () => this.#startAutoplay(ms);

    this.#dom.slider.addEventListener('mouseenter', stop);
    this.#dom.slider.addEventListener('pointerdown', stop);
    this.#dom.slider.addEventListener('mouseleave', resume);
    this.#dom.slider.addEventListener('pointerup', resume);
    this.#dom.slider.addEventListener('touchstart', stop);
    this.#dom.slider.addEventListener('touchend', resume);
  }

  #startAutoplay(ms) {
    this.#stopAutoplay();
    this.autoplayId = setInterval(() => this.embla.scrollNext(), ms);
  }

  #stopAutoplay() {
    if (this.autoplayId) {
      clearInterval(this.autoplayId);
      this.autoplayId = null;
    }
  }

  // -----------------------------
  // CONTROLS
  // -----------------------------
  #initControls() {
    this.#updateControls();
  }

  #updateControls() {
    if (!this.embla) return;

    const canScroll = this.embla.canScrollNext() || this.embla.canScrollPrev();

    if (this.#dom.prev) this.#dom.prev.style.display = canScroll ? 'flex' : 'none';
    if (this.#dom.next) this.#dom.next.style.display = canScroll ? 'flex' : 'none';
    if (this.#dom.dots) this.#dom.dots.style.display = canScroll ? 'flex' : 'none';
  }

  // -----------------------------
  // DOTS
  // -----------------------------
  #initDots() {
    if (this.dataset.dots !== 'true') return;

    if (!this.#dom.dots) {
      this.#dom.dots = document.createElement('div');
      this.#dom.dots.className = 'slider-dots';
      this.#dom.slider.after(this.#dom.dots);
    }

    const originals = this.embla
      .slideNodes()
      .filter((s) => !s.classList.contains('is-clone'));

    this.#dom.dots.innerHTML = '';
    originals.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.className = 'slide-dot';
      dot.type = 'button';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => this.embla.scrollTo(i));
      this.#dom.dots.appendChild(dot);
    });

    this.#dom.dots.firstElementChild?.classList.add('is-selected');
    this.embla.on('select', () => {
      const index = this.embla.selectedScrollSnap();
      [...this.#dom.dots.children].forEach((dot, i) =>
        dot.classList.toggle('is-selected', i === index),
      );
    });
  }

  // -----------------------------
  // ARROWS
  // -----------------------------
  #initArrows() {
    if (this.dataset.arrows !== 'true') return;

    if (!this.#dom.prev) {
      this.#dom.prev = this.#createArrow('prev');
    }
    if (!this.#dom.next) {
      this.#dom.next = this.#createArrow('next');
    }

    this.#dom.prev.addEventListener('click', () => this.embla.scrollPrev());
    this.#dom.next.addEventListener('click', () => this.embla.scrollNext());
  }

  #createArrow(type) {
    const btn = document.createElement('button');
    btn.className = `${type} slide-arrow`;
    btn.setAttribute(`data-${type}`, '');
    btn.setAttribute('aria-label', type === 'prev' ? 'Previous slide' : 'Next slide');
    btn.innerHTML =
      type === 'prev'
        ? `<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M12 15l-5-5 5-5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
           </svg>`
        : `<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M8 5l5 5-5 5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
           </svg>`;
    this.#dom.slider.appendChild(btn);
    return btn;
  }

  // -----------------------------
  // CUSTOM PAGINATION
  // -----------------------------
  #initCustomPagination() {
    if (!this.#dom.slider.hasAttribute('data-custom-pag')) return;
    if (!this.#dom.prevText || !this.#dom.nextText) return;

    const update = () => {
      const slides = this.embla.slideNodes();
      const i = this.embla.selectedScrollSnap();
      const next = (i + 1) % slides.length;
      const prev = (i - 1 + slides.length) % slides.length;

      this.#dom.prevText.textContent = slides[prev]?.dataset.title || '';
      this.#dom.nextText.textContent = slides[next]?.dataset.title || '';
    };

    this.embla.on('select', update);
    update();
  }

  // -----------------------------
  // EDGE MARKING
  // -----------------------------
  #markEdges() {
    if (!this.embla) return;

    const viewport = this.#dom.slider.getBoundingClientRect();
    const snaps = this.embla.scrollSnapList();
    const progress = this.embla.scrollProgress();

    this.embla.slideNodes().forEach((slide, i) => {
      slide.classList.remove(
        'near-left-edge',
        'near-right-edge',
        'extreme-left-edge',
        'extreme-right-edge',
        'is-center',
      );

      const rect = slide.getBoundingClientRect();
      const center = rect.left + rect.width / 2;

      if (Math.abs(snaps[i] - progress) < 0.001) {
        slide.classList.add('is-center');
      }

      const distLeft = center - viewport.left;
      const distRight = viewport.right - center;

      if (distLeft < bdSlider.#EDGE_DISTANCES.EXTREME) slide.classList.add('extreme-left-edge');
      else if (distRight < bdSlider.#EDGE_DISTANCES.EXTREME) slide.classList.add('extreme-right-edge');
      else if (distLeft < bdSlider.#EDGE_DISTANCES.NEAR) slide.classList.add('near-left-edge');
      else if (distRight < bdSlider.#EDGE_DISTANCES.NEAR) slide.classList.add('near-right-edge');
    });
  }

  // -----------------------------
  // WHEEL HANDLING
  // -----------------------------
  #onWheel(e) {
    if (!this.embla) return;
    if (Math.abs(e.deltaX) < 2) return;
    if (!this.embla.canScrollNext() && !this.embla.canScrollPrev()) return;

    e.preventDefault();
    e.deltaX > 0 ? this.embla.scrollNext() : this.embla.scrollPrev();
  }

  // -----------------------------
  // RESIZE HANDLING
  // -----------------------------
  #handleResize() {
    clearTimeout(this.#resizeTimeout);
    this.#resizeTimeout = setTimeout(() => {
      this.#destroy();
      this.#init();
      
    }, 150);
  }

  // -----------------------------
  // DESTROY
  // -----------------------------
  #destroy() {
    this.#stopAutoplay();
    this.#unsubscribeFromEmblaEvents();

    this.embla?.destroy();
    this.thumbsEmbla?.destroy();

    this.embla = null;
    this.thumbsEmbla = null;

    this.#dom.slider?.removeEventListener('wheel', this.#wheelHandler);
    this.#dom.dots?.remove();
    this.#dom.prev?.remove();
    this.#dom.next?.remove();
  }
}

// Define the custom element
customElements.define('bd-slider', bdSlider);